/**
 * BIP39 mnemonic helpers.
 *
 * The mnemonic is the master secret. It MUST never leave the client device.
 * Only the derived public key is sent to the backend.
 *
 * Kept in sync with `client/src/crypto/mnemonic.ts` — change both together.
 */

import {
  generateMnemonic,
  mnemonicToSeedSync,
  validateMnemonic,
} from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english.js";

export type MnemonicWordCount = 12 | 15 | 18 | 21 | 24;

const STRENGTH_BITS_BY_WORDS: Record<MnemonicWordCount, number> = {
  12: 128,
  15: 160,
  18: 192,
  21: 224,
  24: 256,
};

/** English BIP39 wordlist (re-exported for UI auto-completion). */
export const ENGLISH_WORDLIST = wordlist;

/**
 * Generate a new BIP39 mnemonic. Default is 24 words (256-bit entropy) —
 * recommended for long-term identity keys.
 */
export function generate(words: MnemonicWordCount = 24): string {
  return generateMnemonic(wordlist, STRENGTH_BITS_BY_WORDS[words]);
}

/** Normalise a mnemonic for storage / comparison: trim + collapse whitespace + lowercase. */
export function normalize(mnemonic: string): string {
  return mnemonic.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Check whether a mnemonic is valid (length, words in wordlist, checksum). */
export function isValid(mnemonic: string): boolean {
  return validateMnemonic(normalize(mnemonic), wordlist);
}

/**
 * Convert a mnemonic to its 64-byte seed via PBKDF2.
 * Optional passphrase ("25th word") for extra entropy.
 */
export function toSeed(mnemonic: string, passphrase = ""): Uint8Array {
  const normalised = normalize(mnemonic);
  if (!validateMnemonic(normalised, wordlist)) {
    throw new Error("Invalid mnemonic");
  }
  return mnemonicToSeedSync(normalised, passphrase);
}

/** Get the word count of a mnemonic, after normalisation. */
export function wordCount(mnemonic: string): number {
  return normalize(mnemonic).split(" ").length;
}
